import { useId, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { useOverlayMotion } from '../lib/useOverlayMotion.js';
import { useDialogFocus } from '../lib/useDialogFocus.js';
import { IconClose } from '../icons/index.js';
import { Button } from './Button.js';

/**
 * Modal : la pop-up centrale STANDARD des apps MyTaki.
 *
 * Usage : fenetre ponctuelle de confirmation ou de choix. La creation et
 * l'edition des fiches vivent dans la fiche elle-meme, pas dans un modal.
 *
 * Doctrine : un seul modal ouvert a la fois par ecran (un seul etat openModal
 * par ecran, ex. `useState<'outing' | 'cat' | null>(null)`).
 *
 * Fermeture : clic sur le voile, touche Escape, ou un bouton de l'app dans
 * `footer` qui appelle `onClose`.
 *
 * Motion : ouverture ET fermeture animees d'office (voile en fondu, panneau
 * en pop, utilitaires taki-anim-* du socle). C'est la norme de tout overlay
 * MyTaki : une app n'ecrit JAMAIS d'animation, elle bascule juste `open`.
 */
export interface ModalProps {
  /** Ouvert (rendu) ou non. Rien ne se rend si faux. */
  open: boolean;
  /** Fermeture : clic sur le voile, touche Escape, ou bouton de l'app. */
  onClose: () => void;
  /** Titre du panneau, rendu en tete (h2) et utilise comme nom accessible. */
  title: string;
  /** Precision optionnelle rendue sous le titre. */
  description?: string;
  /**
   * Largeur du panneau : sm w-[25rem], md w-[32rem] (defaut), lg w-[40rem].
   *
   * `xl` EST LA SEULE A POSER AUSSI UNE HAUTEUR, et c'est ce qui la distingue :
   * les trois autres tiennent la largeur et laissent le contenu decider du
   * reste. Une fenetre de TRAVAIL (chercher, filtrer, parcourir un tableau) ne
   * peut pas faire ca : sa hauteur changerait a chaque frappe de recherche, et
   * le pied remonterait sous le doigt qui vise une ligne. Elle garde donc une
   * surface stable, plafonnee sur les grands ecrans, et c'est son corps qui
   * defile.
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Corps (formulaire). Defile si trop haut. */
  children: ReactNode;
  /** Rangee d'actions optionnelle en pied de panneau (boutons @taki/ui). */
  footer?: ReactNode;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'w-[25rem]',
  md: 'w-[32rem]',
  lg: 'w-[40rem]',
  xl: 'h-[min(42rem,80dvh)] w-[min(72rem,calc(100vw-2rem))]',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
}: ModalProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const isWorkspace = size === 'xl';

  // Cycle de vie anime (norme du socle) : quand `open` passe a faux, le
  // modal reste monte le temps de l'animation de sortie (phase `closing`,
  // classes taki-anim-*-out) puis disparait.
  const { mounted, closing } = useOverlayMotion(open);

  useDialogFocus(open && mounted && !closing, panelRef, onClose, 30);

  if (!mounted) return null;

  return (
    // z-30 : SOUS le lanceur rapide du shell (z-40), pour qu'il puisse
    // s'ouvrir par-dessus un modal d'app.
    <div
      className={cn(
        'fixed inset-0 z-30 flex items-center justify-center p-4',
        // Pendant la sortie, l'overlay laisse passer les clics : l'ecran
        // redessous est utilisable sans attendre la fin du fondu.
        closing && 'pointer-events-none',
      )}
    >
      {/* Voile : token translucide dedie (bg-scrim), jamais `bg-bg/xx`. */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-scrim backdrop-blur-sm',
          closing ? 'taki-anim-fade-out' : 'taki-anim-fade',
        )}
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description != null ? descriptionId : undefined}
        className={cn(
          'relative z-10 flex max-w-full flex-col outline-none',
          SIZE_CLASSES[size],
          'rounded-2xl border border-border bg-surface shadow-panel',
          isWorkspace ? 'max-h-[calc(100dvh-2rem)]' : 'max-h-[80%] p-4',
          closing ? 'taki-anim-pop-out' : 'taki-anim-pop',
        )}
      >
        {isWorkspace ? (
          <header className="flex shrink-0 flex-col gap-1 px-6 pb-5 pt-5 pr-16">
            <h2 id={titleId} className="text-lg font-semibold text-ink-900">
              {title}
            </h2>
            {description != null && (
              <p id={descriptionId} className="text-xs text-ink-500">
                {description}
              </p>
            )}
          </header>
        ) : (
          <div className="shrink-0 pb-3">
            <h2 id={titleId} className="text-sm font-semibold text-ink-900">
              {title}
            </h2>
            {description != null && (
              <p id={descriptionId} className="mt-1 text-xs text-ink-500">
                {description}
              </p>
            )}
          </div>
        )}

        {/* Corps : seule partie qui defile si le contenu est trop haut. */}
        <div className={cn('min-h-0 flex-1 overflow-y-auto', isWorkspace && 'px-6 pb-5')}>
          {children}
        </div>

        {footer != null && (
          <div
            className={cn(
              'flex shrink-0 items-center justify-end gap-2',
              isWorkspace ? 'border-t border-border-soft px-6 py-4' : 'pt-3',
            )}
          >
            {footer}
          </div>
        )}

        {isWorkspace && (
          /* Rendu apres le corps : le premier focus reste celui de la recherche. */
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Fermer"
            title="Fermer"
            className="absolute right-4 top-4 w-8 px-0"
          >
            <IconClose size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}

export default Modal;
